import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { RenderQueue } from "@/components/engine/RenderQueue";
import { StemAutoRouting } from "@/components/engine/StemAutoRouting";
import { SocialScheduler } from "@/components/engine/SocialScheduler";

export const TheContentEngine = () => {
  const [renderTasks, setRenderTasks] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [stemRoutingEnabled, setStemRoutingEnabled] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, postsRes] = await Promise.all([
        axios.get(`${API}/render-tasks`),
        axios.get(`${API}/social-posts`),
      ]);
      setRenderTasks(tasksRes.data);
      setSocialPosts(postsRes.data);
    } catch (error) {
      console.error("Failed to fetch engine data:", error);
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const response = await axios.put(`${API}/render-tasks/${taskId}`, updates);
      setRenderTasks((prev) =>
        prev.map((t) => (t.id === taskId ? response.data : t))
      );
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleCreateTask = async (taskData) => {
    try {
      const response = await axios.post(`${API}/render-tasks`, taskData);
      setRenderTasks((prev) => [...prev, response.data]);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleCreatePost = async (postData) => {
    try {
      const response = await axios.post(`${API}/social-posts`, postData);
      setSocialPosts((prev) => [...prev, response.data]);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/social-posts/${postId}`);
      setSocialPosts((prev) => prev.filter((p) => p.id !== postId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleStemRoutingToggle = (enabled) => {
    setStemRoutingEnabled(enabled);
    // This would trigger the stem_auto_routing hook in a real implementation
    console.log("STEM_ROUTING_HOOK:", enabled ? "enabled" : "disabled");
  };

  return (
    <div className="space-y-6" data-testid="the-content-engine-page">
      {/* Page Header */}
      <header className="mb-8">
        <h1 className="font-sans font-black text-4xl text-meta-text tracking-tight">
          Content Engine
        </h1>
        <p className="text-meta-text-secondary mt-1">
          Automation & Rendering Pipeline
        </p>
      </header>

      {/* Stem Auto-Routing Toggle */}
      <StemAutoRouting
        enabled={stemRoutingEnabled}
        onToggle={handleStemRoutingToggle}
      />

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Render Queue - Takes 2 columns */}
        <div className="xl:col-span-2">
          <RenderQueue
            tasks={renderTasks}
            onUpdateTask={handleUpdateTask}
            onCreateTask={handleCreateTask}
          />
        </div>

        {/* Social Scheduler */}
        <div className="xl:col-span-1">
          <SocialScheduler
            posts={socialPosts}
            onCreatePost={handleCreatePost}
            onDeletePost={handleDeletePost}
          />
        </div>
      </div>
    </div>
  );
};

export default TheContentEngine;
